import React, { useContext, useEffect, useState } from 'react';
import { localStorageService } from '@/utils/local_storage';
import { some, each, map } from 'lodash';
import { UserContext } from '@/components/pages/assessments/context/user.context';
import dynamic from 'next/dynamic';
const ReferralCart = dynamic(() => import('@/components/Referrals/ReferralCart/ReferralCart'), { ssr: false });
import { modalContext } from '@/context/modal.context';
import { languageContext } from '@/context/language.context';
import classes from './referral.context.module.scss';
import useMixpanel from '@/hooks/useMixpanel';
import useLog from '@/hooks/useLog';
import Referral from '@/utils/api/odas/referral';
import AgencyClientApi from '@/utils/api/odas/agency_client';
import ODRS from '@/utils/api/odrs';
import { resourceHelpers } from '@/utils/helpers';
import ReferralNetwork from '@/utils/referral_network';

export const referralContext = React.createContext({});

// project prop is used to handle special settings based on where the referral sidebar will be used
// currently this only focuses on changing the positioning based on the height of the fixed header
export const ReferralProvider = ({ children, hideReferralCart, project = 'onedegree' }) => {
  const [referredResourceData, setReferredResourceData] = useState({});
  const [inReferralState, setInReferralState] = useState(false);
  const [referredOpportunities, setReferredOpportunities] = useState([]);
  const [returnToPage, setReturnToPage] = useState('');
  const [referralClient, setReferralClient] = useState({});
  const [, setReferralOpportunitiesNotes] = useState([]);

  useEffect(() => {
    setReferredResourceData(localStorageService.get('referredResourceData') ?? {});
    setInReferralState(localStorageService.get('referralState') ?? false);
    setReferredOpportunities(localStorageService.get('referralOpportunities') ?? []);
    setReturnToPage(localStorageService.get('returnToPage') ?? '');
    setReferralClient(localStorageService.get('referralClient') ?? {});
    setReferralOpportunitiesNotes(localStorageService.get('referralOpportunitiesNotes') ?? []);
  }, []);

  // These internal helpers ensure React state and localStorage are always updated together.
  const persistReferredResourceData = (val) => {
    setReferredResourceData(val);
    localStorageService.set('referredResourceData', val);
  };
  const persistInReferralState = (val) => {
    setInReferralState(val);
    localStorageService.set('referralState', val);
  };
  const persistReferredOpportunities = (val) => {
    setReferredOpportunities(val);
    localStorageService.set('referralOpportunities', val);
  };
  const persistReturnToPage = (val) => {
    setReturnToPage(val);
    localStorageService.set('returnToPage', val);
  };
  const persistReferralClient = (val) => {
    setReferralClient(val);
    localStorageService.set('referralClient', val);
  };
  const persistReferralOpportunitiesNotes = (val) => {
    setReferralOpportunitiesNotes(val);
    localStorageService.set('referralOpportunitiesNotes', val);
  };

  const { agencyClient } = useContext(UserContext);
  const showCart = inReferralState && hideReferralCart !== true;
  const [recentlyAddedReferral, setRecentlyAddedReferral] = useState(false);
  const [completedAssessmentsLoading, setCompletedAssessmentsLoading] = useState(false);
  const { track } = useMixpanel();
  const { log } = useLog();
  const { enqueueModal } = useContext(modalContext);
  const { currentLanguage } = useContext(languageContext);

  // When a referralClient is set (e.g. from Angular flow or React search results),
  // the client data comes from the index action which omits completed_assessments.
  // Fetch the full client from the show endpoint to get completed_assessments so
  // the intake assessment completion check works correctly.
  useEffect(() => {
    if (referralClient?.id && !referralClient?.completed_assessments) {
      setCompletedAssessmentsLoading(true);
      AgencyClientApi.loadAgencyClient({ id: referralClient.id, params: { include_completed_assessments: true } })
        .then((resp) => {
          const fullClient = resp?.data?.agency_client;
          if (fullClient?.completed_assessments) {
            persistReferralClient({
              ...referralClient,
              completed_assessments: fullClient.completed_assessments
            });
          }
        })
        .catch(() => {})
        .finally(() => setCompletedAssessmentsLoading(false));
    }
  }, [referralClient?.id, referralClient?.completed_assessments]);

  useEffect(() => {
    if (referredResourceData?.referralId) {
      Referral.loadReferral(referredResourceData.referralId).then((resp) => {
        if (resp.status == 200 || resp.status == 201) {
          enqueueModal(
            {
              name: 'ReferralSentModal',
              title: 'Referral sent!',
              resource: resp.data?.referral,
              referralClientInfo: referredResourceData.referralClientInfo
            },
            { forceOpen: true }
          );
        }
      });
    }
  }, [referredResourceData]);

  const addReferredOpportunity = (resource) => {
    if (!isReferred(resource)) {
      // agency_id comes from the freshly-persisted referral client. updateInReferralState writes it
      // to localStorage synchronously just before this runs, so read it from there rather than the
      // referralClient state, which hasn't re-rendered yet on the first add of a new referral.
      const agency_id = (localStorageService.get('referralClient') || referralClient)?.agency_id;
      track('Added resource to referral', { agency_id, resource_title: resource.title, resource_id: resource.id, resource_type: 'Opportunity' });
      persistReferredOpportunities([...referredOpportunities, resourceHelpers.replaceWithTranslations(resource)]);
      setRecentlyAddedReferral((prevState) => {
        if (prevState) {
          clearTimeout(prevState);
        }
        return setTimeout(() => {
          setRecentlyAddedReferral(false);
        }, 4000);
      });
    }
  };

  // This should be called even if the client page didn't load correctly, when the referral is submitted.
  const clearReferralInfo = () => {
    persistReferredOpportunities([]);
    persistReferralClient({});
    persistReferralOpportunitiesNotes([]);
  };

  /**
   * Return list of org ids that opt out of closed-loop referrals and only want one-way
   * referrals in all cases, regardless of the referring org or the referral network.
   *
   * TODO shouldn't we allow CLRs for any other networks they might be in?
   */
  const getOneWayOnlyRestrictions = (summaries) => {
    const ok = {}; // map network+relationship to boolean whether CLRs are allowed
    const disallowed = {}; // if any opps are being referred to these org ids, they will need to be one-way, not CLRs
    const added = {};
    const add = (orgId, networkId) => {
      const key = `${networkId}_${orgId}`;
      if (!added[key]) {
        added[key] = true;
        disallowed[orgId] ||= [];
        disallowed[orgId].push({
          networkId,
          ownerOrgName: summaries[networkId].network_owner.name,
          networkName: summaries[networkId].network_config.name,
          restrictionTranslationKey: 'Closed-loop referrals are not allowed to this organization. TODO Should we have messaging about this?'
        });
      }
    };
    each(summaries, (net, netId) => {
      net.in_network_agencies.forEach(({ referral_network_id, relationship, fetchable_id }) => {
        const relationshipNetworkKey = `${referral_network_id}:${relationship}`;
        if (ok[relationshipNetworkKey] === false) {
          // already determined this orgs with this relationship key should be disallowed
          add(fetchable_id, referral_network_id);
        } else if (typeof ok[relationshipNetworkKey] === 'undefined') {
          if (ReferralNetwork.hasCLROption(referral_network_id, 'referrals:one_way_only_to:' + relationship)) {
            ok[relationshipNetworkKey] = false;
            add(fetchable_id, referral_network_id);
          } else {
            // closed-loop referrals are allowed to orgs in this network with this relationship
            ok[relationshipNetworkKey] = true;
          }
        }
      });
    });
    return disallowed;
  };

  const getOpportunitiesCart = ({ affiliationOrgId, networkAgencies, partnerOrgIds, oneWayOnly }) => {
    // inNetworkOpps and outOfNetworkOpps are lists of objects with an `opportunity`, and other data in the case of in-network opps
    const inNetworkOpps = [];
    const outOfNetworkOpps = [];
    const oppIdNetworkMap = {}; // oppId => [networkIds]
    const inPartnerNetwork = {}; // oppId => true if can be assigned the meta "partner network"

    // split the list of opportunities into in-or-out-of-network buckets
    each(referredOpportunities, (opportunity) => {
      const oppId = opportunity.id;
      const orgId = opportunity.organization.id;
      let found = false;
      // placeholder for checking partner orgs
      if (partnerOrgIds.includes(opportunity.organization.id)) {
        inPartnerNetwork[opportunity.id] = true;
        inNetworkOpps.push({ opportunity, partner: true });
        found = true;
      }
      // Iterate over the network orgs unless this opp is within my org
      if (orgId !== affiliationOrgId) {
        each(networkAgencies, (network, networkId) => {
          const disallowedCLR = !!oneWayOnly[orgId]?.find((restriction) => restriction.networkId === parseInt(networkId));
          if (disallowedCLR) return; // skip if this referral is only allowed to be one-way (not a closed-loop referral)
          // look in traditional CLR networks
          const isInNetwork = ReferralNetwork.isResourceInNetwork(opportunity, map(network.in_network_agencies, 'fetchable_id'), { byId: true });
          if (isInNetwork) {
            oppIdNetworkMap[oppId] ||= [];
            oppIdNetworkMap[oppId].push(networkId);
            if (!found) {
              inNetworkOpps.push({ opportunity });
            }
            found = true;
          }
        });
      }

      if (!found) {
        outOfNetworkOpps.push({ opportunity });
      }
    });

    // add the list of available networks for opps that qualify as CLRs
    const insideNetwork = inNetworkOpps.map((record) => {
      const opportunity = record.opportunity;
      record.networks = (oppIdNetworkMap[opportunity.id] || []).map((networkId) => ReferralNetwork.getReferralNetworkById(networkId));
      if (inPartnerNetwork[opportunity.id]) {
        record.networks.unshift(ReferralNetwork.getPartnerNetwork()); // add option to choose the partner network
      }
      // set selected if there is only one network option
      record.selectedReferralNetworkId = record.networks.length === 1 ? record.networks[0].id : null;
      return record;
    });

    return {
      insideNetwork,
      outsideNetwork: outOfNetworkOpps
    };
  };

  const isReferred = (resource) => {
    if (!resource) return false;
    if (resource.resource_type == 'Opportunity') {
      return some(referredOpportunities, { id: resource.id });
    } else {
      return some(referredOpportunities, (opp) => {
        return opp.organization && opp.organization.id == resource.id;
      });
    }
  };

  const removeReferredOpportunity = (resource) => {
    if (isReferred(resource)) {
      persistReferredOpportunities(referredOpportunities.filter((item) => item.id !== resource.id));
    }
  };

  const refreshOpportunitiesTranslations = () => {
    if (!referredOpportunities.length) return;
    const ids = referredOpportunities.map((opp) => opp.id);
    // fetch the resources in the current language
    ODRS.getResourcesById('opportunities', ids, { locale: currentLanguage }).then((resp) => {
      if (resp?.data?.opportunities) persistReferredOpportunities(resp.data.opportunities);
    });
  };

  useEffect(() => {
    refreshOpportunitiesTranslations();
  }, [currentLanguage]);

  const updateInReferralState = (newState, clientObj, source = '') => {
    const val = !!newState; // force it to be a boolean
    // Matches Angular's referral_button.setClient: 'Started referral' fires once when a pro picks a
    // client to begin a referral. `source`/`via` record where it started (opp page => 'opportunity',
    // '' elsewhere). Guarded on the not-in-referral -> in-referral transition so adding further
    // opportunities to an already-active referral doesn't re-fire it.
    if (val && !inReferralState && clientObj) {
      track('Started referral', { agency_id: clientObj.agency_id, source });
      log({
        event: 'Started referral',
        message: 'single',
        object_type: 'ClientReferral',
        agency_id: clientObj.agency_id,
        client_id: clientObj.id,
        via: source
      });
    }
    persistInReferralState(val);
    if (val === false) {
      clearReferralInfo();
    } else if (clientObj) {
      persistReferralClient(clientObj);
    }
  };

  const getReferralClient = () => {
    if (project == 'interface' && agencyClient) {
      persistReferralClient(agencyClient);
      return agencyClient;
    } else {
      return referralClient || {};
    }
  };

  const contentClasses = [showCart ? classes.referralSpacing : '', classes[project] || ''];

  return (
    <referralContext.Provider
      value={{
        addReferredOpportunity,
        clearReferralInfo,
        inReferralState,
        isReferred,
        recentlyAddedReferral,
        referredOpportunities,
        removeReferredOpportunity,
        updateInReferralState,
        referralClient,
        persistReferralClient,
        getReferralClient,
        getOneWayOnlyRestrictions,
        getOpportunitiesCart,
        refreshOpportunitiesTranslations,
        persistReferredResourceData,
        persistReturnToPage,
        returnToPage,
        project,
        completedAssessmentsLoading
      }}
    >
      <>
        <div key="referralSibling" className={contentClasses.join(' ')}>
          {children}
        </div>
        {showCart ? <ReferralCart project={project} /> : null}
      </>
    </referralContext.Provider>
  );
};

export const ReferralConsumer = referralContext.Consumer;
